import { useEffect, useState } from "react";
import axios from "axios";
import { LockKeyhole } from "lucide-react";
import { useLocation } from "react-router-dom";
import { Button } from "./ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SiteAccessGate = ({ children }) => {
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [protectedSite, setProtectedSite] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (location.pathname.startsWith("/dashboard/admin")) {
      setChecking(false);
      setProtectedSite(false);
      return undefined;
    }
    let active = true;
    axios
      .get(`${API_URL}/api/site-access/status`, { withCredentials: true })
      .then(({ data }) => {
        if (!active) return;
        setProtectedSite(Boolean(data.enabled && !data.unlocked));
      })
      .catch(() => {
        if (active) setError("Unable to check site access. Please try again.");
      })
      .finally(() => {
        if (active) setChecking(false);
      });
    return () => {
      active = false;
    };
  }, [location.pathname]);

  const unlock = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await axios.post(
        `${API_URL}/api/site-access/unlock`,
        { password },
        { withCredentials: true }
      );
      setProtectedSite(false);
      setPassword("");
    } catch (unlockError) {
      setError(unlockError.response?.data?.detail || "Incorrect password.");
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return <div className="min-h-screen bg-[#04040a]" aria-label="Checking site access" />;
  }

  if (!protectedSite || location.pathname.startsWith("/dashboard/admin")) return children;

  return (
    <main className="min-h-screen bg-[#04040a] text-white flex items-center justify-center px-6 py-12 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-20" aria-hidden="true">
        <div className="blob bg-[#CCFF00] w-96 h-96 -top-40 -left-32" />
        <div className="blob bg-[#7C3AED] w-80 h-80 -bottom-32 -right-24" />
      </div>
      <section className="relative z-10 w-full max-w-md text-center">
        <div className="font-['Syne'] text-3xl font-extrabold tracking-tighter mb-12">
          Dope<span className="text-[#CCFF00]">Media</span>
        </div>
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-[#CCFF00]/30 bg-[#CCFF00]/10 text-[#CCFF00]">
          <LockKeyhole className="h-7 w-7" aria-hidden="true" />
        </div>
        <h1 className="font-['Syne'] text-3xl sm:text-4xl font-extrabold tracking-tight">This site is currently private.</h1>
        <p className="mt-4 text-zinc-400">Enter the password to continue.</p>
        <form onSubmit={unlock} className="mt-8 space-y-3 text-left">
          <label htmlFor="site-password" className="sr-only">Site password</label>
          <input
            id="site-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder="Password"
            autoComplete="current-password"
            autoFocus
            className="h-14 w-full rounded-xl border border-white/15 bg-white/5 px-4 text-white outline-none transition focus:border-[#CCFF00]"
          />
          <Button type="submit" disabled={submitting || !password} className="h-14 w-full rounded-xl bg-[#CCFF00] text-black hover:bg-[#d9ff66] font-bold">
            {submitting ? "Checking..." : "Enter Site"}
          </Button>
          {error && <p className="text-center text-sm text-red-400" role="alert">{error}</p>}
        </form>
      </section>
    </main>
  );
};

export default SiteAccessGate;
